import React from 'react'
// libs
import styled from 'styled-components'
// hooks
import { useMediaQuery } from '@mui/material'
// components
import Drawer, { DrawerProps } from '@mui/material/Drawer'
// constants
import { SCREEN_SIZE } from 'constants/screenSize'
// utils
import get from 'lodash/get'

const DrawerContent = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 100%;
  display: flex;
  flex-direction: column;
`

export interface DrawerCustomProps extends Omit<DrawerProps, 'ModalProps'> {
  width?: string
  isOpen: boolean
  hideModal: () => void
  ModalProps?: DrawerProps['ModalProps'] & { disableBackdropClick?: boolean }
  contentWrapperStyles?: React.CSSProperties
}

const DrawerModal: React.FC<DrawerCustomProps> = ({
  width,
  isOpen,
  anchor = 'right',
  children,
  hideModal,
  ModalProps = {},
  contentWrapperStyles = {},
  ...rest
}) => {
  const isTablet = useMediaQuery(SCREEN_SIZE.TABLET)

  const { disableBackdropClick, ...modalProps } = ModalProps

  const onModalClose = (_event: React.MouseEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (!disableBackdropClick && reason === 'backdropClick') {
      hideModal()
    }
  }

  // Global drawers (PageModals) remount on navigation while the redux isOpen is still true — the previous
  // instance resets it only in its unmount cleanup. MUI Portal mounts the Slide one render late, so it would
  // start with in=false, never fire onExited, and leave an invisible Modal root blocking all clicks.
  // Opening only after mount lets the Modal start from the "exited" state.
  const [isMounted, setIsMounted] = React.useState(false)

  React.useEffect(() => {
    setIsMounted(true)

    return () => {
      hideModal()
    }
  }, [])

  return (
    <Drawer
      open={isMounted && isOpen}
      anchor={anchor}
      ModalProps={{ ...modalProps, onClose: onModalClose }}
      PaperProps={{ square: true, ...get(rest, 'PaperProps', {}) }}
      {...rest}>
      <DrawerContent $width={isTablet ? '100vw' : width} style={contentWrapperStyles}>
        {children}
      </DrawerContent>
    </Drawer>
  )
}

export default DrawerModal
